import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, AdminLayout } from "./AdminLayout";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Key, Shield, UserCheck, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_STYLES = {
  admin: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30", label: "Admin", icon: Shield },
  editor: { bg: "bg-its-gold/20", text: "text-its-gold", border: "border-its-gold/30", label: "Editor", icon: Edit },
  viewer: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30", label: "Viewer", icon: Eye },
};

export const AdminUsers = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createForm, setCreateForm] = useState({ email: "", password: "", name: "", role: "editor" });
  const [editForm, setEditForm] = useState({ email: "", name: "", role: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });

  const isAdmin = currentUser?.role === "admin";

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch { toast.error("Failed to load users"); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password || !createForm.name) {
      toast.error("All fields are required");
      return;
    }
    try {
      await axios.post(`${API}/users`, createForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User created");
      setShowCreate(false);
      setCreateForm({ email: "", password: "", name: "", role: "editor" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setEditForm({ email: user.email, name: user.name, role: user.role });
    setShowEdit(true);
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User updated");
      setShowEdit(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
  };

  const openPassword = (user) => {
    setSelectedUser(user);
    setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    setShowPassword(true);
  };

  const handlePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      const payload = { new_password: passwordForm.new_password };
      const isSelf = selectedUser.id === currentUser?.id;
      if (isSelf && !isAdmin) {
        payload.current_password = passwordForm.current_password;
      }
      await axios.put(`${API}/users/${selectedUser.id}/password`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password updated");
      setShowPassword(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.name}" (${user.email})?`)) return;
    try {
      await axios.delete(`${API}/users/${user.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User deleted");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="users-title">Users</h1>
          <p className="text-sm text-its-offwhite/60 mt-1">{users.length} user(s)</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2" data-testid="add-user-btn">
            <Plus size={18} /> Add User
          </button>
        )}
      </div>

      <div className="space-y-3">
        {users.map((user) => {
          const style = ROLE_STYLES[user.role] || ROLE_STYLES.viewer;
          const RoleIcon = style.icon;
          const isSelf = user.id === currentUser?.id;
          return (
            <div
              key={user.id}
              className="flex items-center gap-4 p-4 rounded-lg bg-its-teal/50 border border-its-teal-light/10"
              data-testid={`user-row-${user.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white">{user.name}</span>
                  {isSelf && <span className="text-xs text-its-offwhite/40">(you)</span>}
                </div>
                <p className="text-sm text-its-offwhite/60">{user.email}</p>
              </div>

              <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
                <RoleIcon size={14} />
                {style.label}
              </span>

              {/* Password change — admin can change any, users can change their own */}
              {(isAdmin || isSelf) && (
                <button
                  onClick={() => openPassword(user)}
                  className="p-2 text-its-offwhite/60 hover:text-its-gold transition-colors"
                  title="Change password"
                >
                  <Key size={18} />
                </button>
              )}

              {/* Edit — admin can edit any, editors can edit themselves */}
              {(isAdmin || isSelf) && (
                <button
                  onClick={() => openEdit(user)}
                  className="p-2 text-its-offwhite/60 hover:text-white transition-colors"
                  title="Edit user"
                >
                  <UserCheck size={18} />
                </button>
              )}

              {/* Delete — admin only, can't delete self */}
              {isAdmin && !isSelf && (
                <button
                  onClick={() => handleDelete(user)}
                  className="p-2 text-its-offwhite/60 hover:text-red-400 transition-colors"
                  title="Delete user"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-its-teal-dark border-its-teal-light/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="form-label">Name</label>
              <input type="text" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="form-input" data-testid="create-user-name" />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="form-input" data-testid="create-user-email" />
            </div>
            <div>
              <label className="form-label">Password</label>
              <input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="form-input" data-testid="create-user-password" />
            </div>
            <div>
              <label className="form-label">Role</label>
              <select value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })} className="form-input" data-testid="create-user-role">
                <option value="admin">Admin — Full access + user management</option>
                <option value="editor">Editor — Manage all content</option>
                <option value="viewer">Viewer — View only</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleCreate} className="btn-primary" data-testid="save-user-btn">Create User</button>
              <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-its-teal-dark border-its-teal-light/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="form-label">Name</label>
              <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="form-input" />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="form-input" />
            </div>
            {isAdmin && (
              <div>
                <label className="form-label">Role</label>
                <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="form-input">
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={handleEdit} className="btn-primary">Save</button>
              <button onClick={() => setShowEdit(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPassword} onOpenChange={setShowPassword}>
        <DialogContent className="bg-its-teal-dark border-its-teal-light/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Change Password{selectedUser && ` — ${selectedUser.name}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Show current password field only for non-admins changing their own */}
            {selectedUser?.id === currentUser?.id && !isAdmin && (
              <div>
                <label className="form-label">Current Password</label>
                <input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} className="form-input" />
              </div>
            )}
            <div>
              <label className="form-label">New Password</label>
              <input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} className="form-input" data-testid="new-password-input" />
            </div>
            <div>
              <label className="form-label">Confirm New Password</label>
              <input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} className="form-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handlePassword} className="btn-primary" data-testid="save-password-btn">Update Password</button>
              <button onClick={() => setShowPassword(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsers;
